import axios from 'axios';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import {
  getToken,
  setToken as storeToken,
  setImpersonating,
  getIsImpersonating,
  clearTokens as clearTokenStore,
} from '../utils/tokenStore';
import { refreshAccessToken } from '../utils/tokenRefresh';

const AuthContext = createContext();
const { Provider } = AuthContext;

function parseJwt(jwt) {
  const base64Url = jwt.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export const KEYS = {
  account: 'passkings-account',
};

function getExpiresAt(token) {
  const payload = parseJwt(token);
  const { exp } = payload;
  return exp;
}

const authChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('passentry-auth')
    : null;

const AuthProvider = ({ children }) => {
  const cachedAccount = localStorage.getItem(KEYS.account);

  const { colorMode, toggleColorMode } = useColorMode();

  const [authState, setAuthState] = useState({
    token: null,
    exp: null,
    account: cachedAccount ? JSON.parse(cachedAccount) : {},
  });

  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const isImpersonation =
      sessionStorage.getItem('impersonation') === 'true';
    const cachedImpersonationAccount =
      sessionStorage.getItem(KEYS.account);
    const cachedSessionAccount = isImpersonation
      ? cachedImpersonationAccount
      : cachedAccount;

    if (!cachedSessionAccount) {
      setAuthLoading(false);
      return;
    }

    const clearSession = () => {
      if (isImpersonation) {
        sessionStorage.removeItem('impersonation');
        sessionStorage.removeItem(KEYS.account);
      } else {
        localStorage.removeItem(KEYS.account);
      }
      setAuthState({ token: null, exp: null, account: {} });
    };

    const hydrateAuth = async () => {
      try {
        const token = await refreshAccessToken();
        if (token) {
          const exp = getExpiresAt(token);
          const account = JSON.parse(cachedSessionAccount);
          storeToken(token, exp);
          if (isImpersonation) setImpersonating(true);
          setAuthState({ token, exp, account });
        } else {
          clearSession();
        }
      } catch {
        clearSession();
      } finally {
        setAuthLoading(false);
      }
    };

    hydrateAuth();
  }, []);

  useEffect(() => {
    if (!authChannel) return;

    const handleMessage = (e) => {
      if (e.data === 'logout') {
        clearTokenStore();
        localStorage.removeItem(KEYS.account);
        localStorage.removeItem('passkings-mfa-verified');
        setAuthState({ token: null, exp: null, account: {} });
      }
    };

    authChannel.addEventListener('message', handleMessage);
    return () => authChannel.removeEventListener('message', handleMessage);
  }, []);

  const setAuthInfo = ({ token, account }) => {
    const exp = getExpiresAt(token);
    storeToken(token, exp);
    localStorage.setItem(KEYS.account, JSON.stringify(account));
    setAuthState({ token, account, exp });
  };

  const setAccount = (account) => {
    localStorage.setItem(KEYS.account, JSON.stringify(account));
    setAuthState({
      ...authState,
      account,
    });
  };

  const setTokens = ({ token }) => {
    const exp = getExpiresAt(token);
    storeToken(token, exp);
    setAuthState({ ...authState, token, exp });
  };

  const setImpersonationAuthInfo = ({ token, account }) => {
    const exp = getExpiresAt(token);
    storeToken(token, exp);
    setImpersonating(true);
    sessionStorage.setItem('impersonation', 'true');
    sessionStorage.setItem(KEYS.account, JSON.stringify(account));
    setAuthState({ token, account, exp });
  };

  const setImpersonationAccount = (account) => {
    setAuthState({
      ...authState,
      account,
    });
  };

  const setImpersonationTokens = ({ token }) => {
    const exp = getExpiresAt(token);
    storeToken(token, exp);
    setAuthState({
      ...authState,
      token,
      exp,
    });
  };

  const logout = useCallback(() => {
    if (colorMode === 'dark') {
      toggleColorMode();
    }
    const wasImpersonation = getIsImpersonating();

    clearTokenStore();
    setAuthState({ token: null, exp: null, account: {} });

    if (wasImpersonation) {
      sessionStorage.removeItem('impersonation');
      sessionStorage.removeItem(KEYS.account);
    } else {
      localStorage.removeItem(KEYS.account);
      localStorage.removeItem('passkings-mfa-verified');

      if (authChannel) {
        authChannel.postMessage('logout');
      }

      const baseURL =
        process.env.REACT_APP_API_URL_AXIOS ||
        window.env?.REACT_APP_API_URL_AXIOS;
      axios
        .delete(`${baseURL}/accounts/sign_out`, { withCredentials: true })
        .catch(() => {});
    }
  }, [colorMode, toggleColorMode, setAuthState]);

  const isAuthenticated = () => {
    if (!authState.exp) {
      return false;
    }
    return true;
  };

  const isAdmin = () => {
    const roles = authState?.account?.roles || [];
    return roles.includes('ROLE_ADMIN') && isAuthenticated();
  };

  const getAccessToken = () => {
    return getToken();
  };

  function isTokenExpired(token) {
    const exp = getExpiresAt(token);
    return exp * 1000 < Date.now();
  }

  return (
    <Provider
      value={{
        KEYS,
        authState,
        authLoading,
        setAuthState: (authInfo) => setAuthInfo(authInfo),
        setAuthTokens: (tokens) => setTokens(tokens),
        setAuthAccount: (account) => setAccount(account),
        setImpersonationAuthState: (authInfo) =>
          setImpersonationAuthInfo(authInfo),
        setImpersonationAccount: (account) => setImpersonationAccount(account),
        setImpersonationTokens: (tokens) => setImpersonationTokens(tokens),
        logout,
        parseJwt,
        isAuthenticated,
        isAdmin,
        getAccessToken,
        getExpiresAt,
        isTokenExpired,
      }}
    >
      {children}
    </Provider>
  );
};

export { AuthContext, AuthProvider };
